import { z } from 'zod';

import { CursorPaginationMetaSchema, PaginationSchema } from '../../pagination.js';

export const PurchaseQuoteStatusSchema = z.enum([
  'DRAFT',
  'SENT',
  'RESPONDED',
  'CLOSED',
  'CANCELLED',
]);

export const PurchaseQuoteStatusFilterSchema = z.enum([
  'all',
  'DRAFT',
  'SENT',
  'RESPONDED',
  'CLOSED',
  'CANCELLED',
]);

export const ListPurchaseQuotesParamsSchema = z.object({
  placeId: z.uuid(),
});

export const ListPurchaseQuotesQuerySchema = PaginationSchema.extend({
  status: PurchaseQuoteStatusFilterSchema.optional().default('all'),
});

export type ListPurchaseQuotesQuery = z.infer<typeof ListPurchaseQuotesQuerySchema>;

export const PurchaseQuoteListItemSchema = z.object({
  id: z.uuid(),
  placeId: z.uuid(),
  displaySequence: z.number().int().positive(),
  displayNumber: z.string().min(1),
  status: PurchaseQuoteStatusSchema,
  itemCount: z.number().int().nonnegative(),
  supplierCount: z.number().int().nonnegative(),
  serviceQuoteId: z.uuid().nullable(),
  serviceQuoteItemId: z.uuid().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type PurchaseQuoteListItem = z.infer<typeof PurchaseQuoteListItemSchema>;
export type PurchaseQuoteStatus = z.infer<typeof PurchaseQuoteStatusSchema>;
export type PurchaseQuoteStatusFilter = z.infer<typeof PurchaseQuoteStatusFilterSchema>;

export const ListPurchaseQuotesResponseSchema = z.object({
  items: z.array(PurchaseQuoteListItemSchema),
  pagination: CursorPaginationMetaSchema,
});

export type ListPurchaseQuotesResponse = z.infer<typeof ListPurchaseQuotesResponseSchema>;

export const PurchaseQuoteIdParamsSchema = z.object({
  id: z.uuid(),
});

export const OpenPurchaseQuoteBodySchema = z.object({
  serviceQuoteId: z.uuid().nullable().optional(),
  serviceQuoteItemId: z.uuid().nullable().optional(),
});

export type OpenPurchaseQuoteBody = z.infer<typeof OpenPurchaseQuoteBodySchema>;

/** BR-PUR-14 — open RFQ seeded from selected service-quote catalog lines. */
export const OpenPurchaseQuoteFromSelectionBodySchema = z
  .object({
    serviceQuoteId: z.uuid().optional(),
    serviceQuoteItemIds: z.array(z.uuid()).min(1).optional(),
    serviceOrderId: z.uuid().optional(),
    serviceOrderItemIds: z.array(z.uuid()).min(1).optional(),
  })
  .superRefine((body, ctx) => {
    const fromQuote = body.serviceQuoteId !== undefined && body.serviceQuoteItemIds !== undefined;
    const fromOrder = body.serviceOrderId !== undefined && body.serviceOrderItemIds !== undefined;
    if (fromQuote === fromOrder) {
      ctx.addIssue({
        code: 'custom',
        message: 'inform either service quote items or service order items',
      });
    }
  });

export type OpenPurchaseQuoteFromSelectionBody = z.infer<
  typeof OpenPurchaseQuoteFromSelectionBodySchema
>;

export const PurchaseQuotePreferredBrandSchema = z.enum([
  'INDIFFERENT',
  'OEM_OR_PARALLEL',
  'GENUINE',
]);

export type PurchaseQuotePreferredBrand = z.infer<typeof PurchaseQuotePreferredBrandSchema>;

/** BR-PUR-23 — exactly one of place product or generic product; free text stays revoked. */
export const AddPurchaseQuoteItemBodySchema = z
  .object({
    placeProductId: z.uuid().optional(),
    catalogGenericProductId: z.uuid().optional(),
    quantityOrdered: z.string().regex(/^\d+(\.\d{1,3})?$/),
    preferredBrand: PurchaseQuotePreferredBrandSchema.optional(),
  })
  .superRefine((body, ctx) => {
    const hasPlace = body.placeProductId !== undefined;
    const hasGeneric = body.catalogGenericProductId !== undefined;
    if (hasPlace === hasGeneric) {
      ctx.addIssue({
        code: 'custom',
        message: 'exactly one of placeProductId or catalogGenericProductId is required',
        path: hasPlace ? ['catalogGenericProductId'] : ['placeProductId'],
      });
    }
  });

export type AddPurchaseQuoteItemBody = z.infer<typeof AddPurchaseQuoteItemBodySchema>;

export const ImportServiceQuoteIntoPurchaseQuoteBodySchema = z.object({
  serviceQuoteId: z.uuid(),
});

export type ImportServiceQuoteIntoPurchaseQuoteBody = z.infer<
  typeof ImportServiceQuoteIntoPurchaseQuoteBodySchema
>;

export const UpdatePurchaseQuoteItemPreferredBrandBodySchema = z.object({
  preferredBrand: PurchaseQuotePreferredBrandSchema,
});

export type UpdatePurchaseQuoteItemPreferredBrandBody = z.infer<
  typeof UpdatePurchaseQuoteItemPreferredBrandBodySchema
>;

/** BR-PUR-27 — requested qty is a positive integer on standalone DRAFT RFQ. */
export const UpdatePurchaseQuoteItemQuantityBodySchema = z.object({
  quantityOrdered: z.string().regex(/^[1-9]\d*$/),
});

export type UpdatePurchaseQuoteItemQuantityBody = z.infer<
  typeof UpdatePurchaseQuoteItemQuantityBodySchema
>;

export const UpdatePurchaseQuoteItemBodySchema = z
  .object({
    preferredBrand: PurchaseQuotePreferredBrandSchema.optional(),
    quantityOrdered: z
      .string()
      .regex(/^[1-9]\d*$/)
      .optional(),
  })
  .refine((body) => body.preferredBrand !== undefined || body.quantityOrdered !== undefined, {
    message: 'preferredBrand or quantityOrdered is required',
  });

export type UpdatePurchaseQuoteItemBody = z.infer<typeof UpdatePurchaseQuoteItemBodySchema>;

export const PurchaseQuoteItemIdParamsSchema = z.object({
  id: z.uuid(),
  itemId: z.uuid(),
});

export const InviteSupplierToQuoteBodySchema = z.object({
  supplierRoleId: z.uuid(),
});

export type InviteSupplierToQuoteBody = z.infer<typeof InviteSupplierToQuoteBodySchema>;

export const PurchaseQuoteOfferBodySchema = z.object({
  unitPriceCents: z.string().regex(/^\d+$/),
  currency: z.string().length(3),
  brand: z.string().nullable().optional(),
  manufacturerCode: z.string().nullable().optional(),
  barcode: z.string().nullable().optional(),
  availability: z.string().nullable().optional(),
  leadTimeDays: z.number().int().min(0).max(30).nullable().optional(),
  quantityOffered: z.string().regex(/^\d+(\.\d{1,3})?$/),
});

export type PurchaseQuoteOfferBody = z.infer<typeof PurchaseQuoteOfferBodySchema>;

export const RecordSupplierResponseBodySchema = z
  .object({
    purchaseQuoteItemId: z.uuid(),
    supplierRoleId: z.uuid(),
    offers: z.array(PurchaseQuoteOfferBodySchema).optional(),
    unitPriceCents: z.string().regex(/^\d+$/).optional(),
    currency: z.string().length(3).optional(),
    brand: z.string().nullable().optional(),
    manufacturerCode: z.string().nullable().optional(),
    barcode: z.string().nullable().optional(),
    availability: z.string().nullable().optional(),
    leadTimeDays: z.number().int().min(0).max(30).nullable().optional(),
    quantityOffered: z
      .string()
      .regex(/^\d+(\.\d{1,3})?$/)
      .optional(),
  })
  .refine((body) => body.offers !== undefined || body.unitPriceCents !== undefined, {
    message: 'offers or unitPriceCents is required',
  });

export type RecordSupplierResponseBody = z.infer<typeof RecordSupplierResponseBodySchema>;

export const SelectPurchaseQuoteItemBodySchema = z.object({
  purchaseQuoteItemId: z.uuid(),
  supplierRoleId: z.uuid(),
  purchaseQuoteResponseId: z.uuid(),
  quantityAllocated: z
    .string()
    .regex(/^\d+(\.\d{1,3})?$/)
    .optional(),
});

export const SuggestPurchaseQuoteAllocationsBodySchema = z.object({
  criterion: z.enum(['price', 'lead', 'availability']),
});

export type SuggestPurchaseQuoteAllocationsBody = z.infer<
  typeof SuggestPurchaseQuoteAllocationsBodySchema
>;

export type SelectPurchaseQuoteItemBody = z.infer<typeof SelectPurchaseQuoteItemBodySchema>;

export const GeneratePurchaseOrdersFromQuoteResponseSchema = z.object({
  purchaseQuoteId: z.uuid(),
  purchaseOrderIds: z.array(z.uuid()),
});

export type GeneratePurchaseOrdersFromQuoteResponse = z.infer<
  typeof GeneratePurchaseOrdersFromQuoteResponseSchema
>;

/** BR-PUR-43 / BR-PUR-44 — batch over quotes; a repeated id counts once. */
export const PurchaseQuoteBatchBodySchema = z.object({
  purchaseQuoteIds: z.array(z.uuid()).min(1),
});

export type PurchaseQuoteBatchBody = z.infer<typeof PurchaseQuoteBatchBodySchema>;

export const PurchaseQuoteBatchRejectionSchema = z.object({
  code: z.string().min(1),
  message: z.string(),
});

export const GeneratePurchaseOrdersFromQuotesResponseSchema = z.object({
  results: z.array(
    z.discriminatedUnion('outcome', [
      z.object({
        purchaseQuoteId: z.uuid(),
        outcome: z.literal('GENERATED'),
        purchaseOrderIds: z.array(z.uuid()),
      }),
      z.object({
        purchaseQuoteId: z.uuid(),
        outcome: z.literal('REJECTED'),
        reason: PurchaseQuoteBatchRejectionSchema,
      }),
    ]),
  ),
});

export type GeneratePurchaseOrdersFromQuotesResponse = z.infer<
  typeof GeneratePurchaseOrdersFromQuotesResponseSchema
>;

export const ReturnPurchaseQuotesToServiceQuotesResponseSchema = z.object({
  results: z.array(
    z.discriminatedUnion('outcome', [
      z.object({
        purchaseQuoteId: z.uuid(),
        outcome: z.literal('ACCEPTED'),
        serviceQuoteId: z.uuid(),
      }),
      z.object({
        purchaseQuoteId: z.uuid(),
        outcome: z.literal('ALREADY_RETURNED'),
      }),
      z.object({
        purchaseQuoteId: z.uuid(),
        outcome: z.literal('REJECTED'),
        reason: PurchaseQuoteBatchRejectionSchema,
      }),
    ]),
  ),
});

export type ReturnPurchaseQuotesToServiceQuotesResponse = z.infer<
  typeof ReturnPurchaseQuotesToServiceQuotesResponseSchema
>;

export const PurchaseQuoteItemKindSchema = z.enum([
  'PLACE_PRODUCT',
  'GENERIC_PRODUCT',
  'LEGACY_FREE_DESCRIPTION',
]);

export const PurchaseQuoteVehicleContextSchema = z.object({
  vin: z.string().nullable(),
  model: z.string().nullable(),
  year: z.number().int().nullable(),
  engine: z.string().nullable(),
});

export const PurchaseQuoteItemResponseSchema = z.object({
  id: z.uuid(),
  kind: PurchaseQuoteItemKindSchema,
  placeProductId: z.uuid().nullable(),
  catalogGenericProductId: z.uuid().nullable(),
  freeDescription: z.string().nullable(),
  /** Catalog label when placeProductId or generic is set (read enrichment). */
  productName: z.string().nullable(),
  productSku: z.string().nullable(),
  suggestedBrand: z.string().nullable().optional(),
  suggestedManufacturerCode: z.string().nullable().optional(),
  /** BR-PUR-37 — item in «Lubrificantes e Aditivos» category tree. */
  manufacturerCodeOptional: z.boolean(),
  quantityOrdered: z.string(),
  preferredBrand: PurchaseQuotePreferredBrandSchema,
});

export const PurchaseQuoteSupplierInviteResponseSchema = z.object({
  supplierRoleId: z.uuid(),
  tradeName: z.string(),
  paymentTermsKind: z.string().nullable(),
  paymentDays: z.number().int().nullable(),
  paymentTermsLabel: z.string().nullable(),
});

export const PurchaseQuoteOriginPartySchema = z.object({
  partyId: z.uuid(),
  name: z.string(),
  phone: z.string().nullable(),
});

export const PurchaseQuoteOriginVehicleSchema = z.object({
  plate: z.string().nullable(),
  label: z.string(),
  odometerKm: z.number().int().nullable(),
  /** Installed component type names for this workshop link, read at request time. */
  components: z.array(z.string()),
});

export const PurchaseQuoteResponseItemSchema = z.object({
  id: z.uuid(),
  quoteItemId: z.uuid(),
  supplierRoleId: z.uuid(),
  unitPriceCents: z.string(),
  currency: z.string(),
  brand: z.string().nullable(),
  manufacturerCode: z.string().nullable(),
  barcode: z.string().nullable(),
  catalogProductId: z.uuid().nullable(),
  availability: z.string().nullable(),
  leadTimeDays: z.number().int().nullable(),
  quantityOffered: z.string(),
});

export const PurchaseQuoteSelectionResponseSchema = z.object({
  quoteItemId: z.uuid(),
  supplierRoleId: z.uuid(),
  responseId: z.uuid(),
  quantityAllocated: z.string(),
});

export const PurchaseQuoteResponseSchema = z.object({
  id: z.uuid(),
  placeId: z.uuid(),
  displaySequence: z.number().int().positive(),
  displayNumber: z.string().min(1),
  serviceQuoteId: z.uuid().nullable(),
  serviceQuoteItemId: z.uuid().nullable(),
  serviceOrderId: z.uuid().nullable().optional(),
  status: PurchaseQuoteStatusSchema,
  originParty: PurchaseQuoteOriginPartySchema.nullable(),
  originVehicle: PurchaseQuoteOriginVehicleSchema.nullable(),
  vehicleContext: PurchaseQuoteVehicleContextSchema,
  items: z.array(PurchaseQuoteItemResponseSchema),
  supplierInvites: z.array(PurchaseQuoteSupplierInviteResponseSchema),
  responses: z.array(PurchaseQuoteResponseItemSchema),
  selections: z.array(PurchaseQuoteSelectionResponseSchema),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type PurchaseQuoteResponse = z.infer<typeof PurchaseQuoteResponseSchema>;
